import type React from 'react';
import * as Y from 'yjs';
import { NODE_MIN_SIZE, FRAME_COLLAPSED_SIZE } from '../../utils/constants';
import { canvasRoots, type CanvasRoots } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';
import { mapToObject, useYjsSnapshot } from '@/lib/sync/use-yjs-snapshot';

/**
 * The shape React Flow expects on its `nodes` prop. Canvas's per-type `data`
 * is whatever the matching node component reads — we pass it through as-is.
 */
export interface RFCanvasNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: Record<string, unknown>;
  /** Initial node dimensions — picked up by React Flow's measurement loop.
   *  Without this, RF measures from the rendered DOM, which is 0×0 for
   *  empty nodes (e.g. freedraw before its SVG sizes itself). */
  width?: number;
  height?: number;
  /** Reserved for hidden / locked / draggable hints; populated in later sessions. */
  draggable?: boolean;
  selectable?: boolean;
  /** RF native field: when true, the node is removed from the rendered set
   *  and excluded from selection / hit-testing. Set on direct children of a
   *  frame whose data.collapsed is true so closed frames hide what's inside. */
  hidden?: boolean;
  parentId?: string;
  /**
   * React Flow v12 parent-child contract: children of a frame have
   * `extent: 'parent'` so RF clamps drag and translates child positions
   * relative to the parent. Top-level nodes leave this undefined.
   */
  extent?: 'parent';
  style?: React.CSSProperties;
  zIndex?: number;
}

function snapshotNode(node: Y.Map<unknown>): RFCanvasNode | null {
  const id = node.get('id');
  const type = node.get('type');
  const position = node.get('position');
  const data = node.get('data');
  if (typeof id !== 'string' || typeof type !== 'string') return null;
  if (
    position === null || typeof position !== 'object' ||
    typeof (position as { x: unknown }).x !== 'number' ||
    typeof (position as { y: unknown }).y !== 'number'
  ) return null;

  // Pass-through Y.Map data — Y.Text fields stay as live refs so node
  // components can render them collaboratively. React Flow does not
  // introspect this object.
  const dataObj: Record<string, unknown> = data instanceof Y.Map ? mapToObject(data) : {};

  const parentId = node.get('parentId');
  const size = node.get('size');
  const locked = node.get('locked');
  const sizeFromYjs =
    size !== null && typeof size === 'object' &&
    typeof (size as { width: unknown }).width === 'number' &&
    typeof (size as { height: unknown }).height === 'number'
      ? size as { width: number; height: number }
      : null;
  // Frame collapse override: when a frame's data.collapsed is true, render
  // it at the user's stored data.collapsedSize (or FRAME_COLLAPSED_SIZE
  // as the design-system default when never resized). The Yjs `size`
  // field is preserved so reopening restores the user's expanded layout;
  // `data.collapsedSize` is the parallel slot for the folder dimensions
  // while collapsed. Both states remember their own size.
  const isCollapsedFrame =
    type === 'frame' && data instanceof Y.Map && data.get('collapsed') === true;
  let collapsedSize: { width: number; height: number } | null = null;
  if (isCollapsedFrame && data instanceof Y.Map) {
    const cs = data.get('collapsedSize');
    if (
      cs && typeof cs === 'object' &&
      typeof (cs as { width: unknown }).width === 'number' &&
      typeof (cs as { height: unknown }).height === 'number'
    ) {
      collapsedSize = cs as { width: number; height: number };
    }
  }
  const sizeObj = isCollapsedFrame
    ? (collapsedSize ?? FRAME_COLLAPSED_SIZE)
    : sizeFromYjs;
  const parentIdStr = typeof parentId === 'string' ? parentId : undefined;
  // RF v12 uses style.width/height on the node wrapper element to size nodes.
  // NodeResizer fires 'dimensions' changes that write back to Yjs size; on the
  // next Yjs snapshot we read size back as style so the DOM matches.
  // Exception: text nodes must NOT have a fixed style size — their wrapper must
  // always be measured from the DOM so the selection box grows as content grows.
  const sizeStyle: React.CSSProperties = (sizeObj && type !== 'text')
    ? { width: sizeObj.width, height: sizeObj.height }
    : {};
  const combinedStyle = { ...sizeStyle };
  // Propagate `locked` into the data payload so renderers can surface it via
  // aria-disabled (M4 carryover). React Flow already reads the top-level
  // `draggable: false` for interaction-blocking; the data field is only for
  // a11y semantics.
  if (locked === true) dataObj.locked = true;
  // Fallback dimensions for unsized nodes so RF can hit-test handles
  // immediately on mount, before the ResizeObserver pass measures real CSS
  // dimensions. Without these, the connector handles were unhittable
  // for fresh nodes until something forced a remeasure (e.g. a resize).
  // The values come from NODE_MIN_SIZE — they're a hint to RF's geometry
  // engine, NOT applied to the DOM as style.width/style.height.
  const minSize = NODE_MIN_SIZE[type as keyof typeof NODE_MIN_SIZE];
  const fallbackW = sizeObj?.width ?? minSize?.width;
  const fallbackH = sizeObj?.height ?? minSize?.height;
  return {
    id,
    type,
    position: { x: (position as { x: number }).x, y: (position as { y: number }).y },
    data: dataObj,
    width: type !== 'text' ? fallbackW : undefined,
    height: type !== 'text' ? fallbackH : undefined,
    ...(Object.keys(combinedStyle).length > 0 ? { style: combinedStyle } : {}),
    draggable: locked !== true,
    parentId: parentIdStr,
    // React Flow parent-child contract: children of a frame need
    // extent:'parent' so RF translates drag deltas relative to the parent.
    ...(parentIdStr !== undefined ? { extent: 'parent' as const } : {}),
  };
}

/**
 * Subscribe to `doc.getMap('nodes')` and produce a snapshot array suitable for
 * React Flow's `nodes` prop. Re-derives on every observed change. Used by
 * CanvasEngine.
 *
 * Authority contract:
 * - `parentId` is authoritative for the parent-child relationship.
 * - `childOrder` (on the parent frame's data) is authoritative for the render
 *   order of children within a frame. Children not listed in childOrder are
 *   appended at the end in Y.Map iteration order. This aligns with M3's
 *   canvas-observer which extracts children in childOrder order.
 */
export function useNodesFromYjs(doc: Y.Doc): RFCanvasNode[] {
  const binding = useCanvasBinding();
  const roots = canvasRoots(doc, binding);
  return useYjsSnapshot<RFCanvasNode[]>(
    () => deriveSnapshot(roots),
    (refresh) => {
      const nodesMap = roots.nodes;
      const zOrderArr = roots.zOrder;
      // observeDeep so nested Y.Map field changes (position, data.color, …) trigger.
      nodesMap.observeDeep(refresh);
      // zOrder changes (bring forward/backward) don't touch nodes map — observe separately.
      zOrderArr.observe(refresh);
      return () => {
        nodesMap.unobserveDeep(refresh);
        zOrderArr.unobserve(refresh);
      };
    },
    [doc],
  );
}

function deriveSnapshot(roots: CanvasRoots): RFCanvasNode[] {
  const nodesMap = roots.nodes;

  // Pre-build the z-index lookup once (was previously rebuilt mid-loop).
  const zOrderArr = roots.zOrder.toArray();
  const zPosIndex = zOrderArr.length > 0
    ? new Map(zOrderArr.map((id, i) => [id, i]))
    : null;

  // Single pass over nodesMap: snapshot each node AND collect the per-frame
  // childOrder + collapsed-frame metadata in the same iteration. Previously
  // the function ran nodesMap.forEach twice and looped over the resulting
  // node array three more times — for large canvases that's quadratic in
  // the worst case.
  const snapshots: RFCanvasNode[] = [];
  const frameChildOrder = new Map<string, string[]>();
  const collapsedFrameIds = new Set<string>();

  nodesMap.forEach((value) => {
    if (!(value instanceof Y.Map)) return;
    const snap = snapshotNode(value);
    if (snap) {
      // Apply zIndex inline so the second pass over `out` is no longer needed.
      if (zPosIndex) snap.zIndex = zPosIndex.get(snap.id) ?? 0;
      snapshots.push(snap);
    }
    // Collect childOrder + collapsed-frame ids for the same Y.Map. We do this
    // unconditionally on every Y.Map (not gated by snapshot success) so a
    // frame whose snapshot failed still surfaces its child-ordering metadata
    // for any children whose snapshots did succeed — keeps the previous
    // behavior where the second nodesMap.forEach didn't depend on snapshotNode.
    const id = value.get('id');
    const data = value.get('data');
    if (typeof id === 'string' && data instanceof Y.Map) {
      const co = data.get('childOrder');
      if (co instanceof Y.Array) {
        frameChildOrder.set(id, co.toArray());
      }
      if (value.get('type') === 'frame' && data.get('collapsed') === true) {
        collapsedFrameIds.add(id);
      }
    }
  });

  // Single post-pass over `snapshots`: drop dangling parentId, apply
  // hidden+extent based on collapsed parents, AND partition into top-level
  // vs by-parent groups. Replaces three separate loops with one.
  //
  // Schema invariant 9: top-level nodes whose parentId references a
  // non-existent frame are treated as parentId: undefined at render time.
  // (Note: circular parentId references like A->B + B->A are NOT defended
  // here — both nodes pass through to RF with extent:'parent'. Requires a
  // buggy write to produce; if it becomes real we can add a cycle walk.)
  //
  // Hidden-children rationale: must set hidden=false explicitly (not just
  // leave undefined) for children of expanded frames — the engine's
  // MergeMap caches local fields across snapshots, so an old hidden:true
  // would persist on re-expand. Also drop extent:'parent' on hidden
  // children: with the frame rendering at 80×80, RF would clamp child
  // positions into the folder rectangle and emit position changes that
  // CanvasEngine.onNodesChange would persist, destroying the open layout.
  const presentIds = new Set<string>();
  for (const node of snapshots) presentIds.add(node.id);

  const topLevel: RFCanvasNode[] = [];
  const byParent = new Map<string, RFCanvasNode[]>();

  for (const node of snapshots) {
    if (node.parentId !== undefined && !presentIds.has(node.parentId)) {
      node.parentId = undefined;
      node.extent = undefined;
    }
    if (node.parentId !== undefined) {
      const parentCollapsed = collapsedFrameIds.has(node.parentId);
      node.hidden = parentCollapsed;
      if (parentCollapsed) node.extent = undefined;

      let group = byParent.get(node.parentId);
      if (!group) { group = []; byParent.set(node.parentId, group); }
      group.push(node);
    } else {
      topLevel.push(node);
    }
  }

  // Order children within each parent by the parent's childOrder; children
  // not listed are appended in Y.Map iteration order.
  const result: RFCanvasNode[] = [...topLevel];
  for (const [parentId, children] of byParent) {
    const order = frameChildOrder.get(parentId) ?? [];
    const orderIndex = new Map(order.map((id, i) => [id, i]));
    const inOrder: RFCanvasNode[] = [];
    const notInOrder: RFCanvasNode[] = [];
    for (const c of children) {
      if (orderIndex.has(c.id)) inOrder.push(c);
      else notInOrder.push(c);
    }
    inOrder.sort((a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0));
    result.push(...inOrder, ...notInOrder);
  }

  return result;
}
